import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { WifiOff, Ticket } from "lucide-react";
import { PageTitle } from "@/components/SettingsPanel";
import { BookingRow, TicketDialog } from "@/components/BookingTicket";
import { ReasonDialog } from "@/components/BookingStatus";
import { GuestsChangeDialog } from "@/components/GuestsChangeDialog";
import { RestrictionBanner } from "@/components/RestrictionBanner";
import { fmtDateTime } from "@/lib/time";
import { api, errorText } from "@/lib/api";
import { loadCachedBookings, saveCachedBookings } from "@/lib/bookingCache";
import { useOnline } from "@/hooks/useOnline";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { Booking, User } from "@/types";
import { useLive } from "@/lib/live";
import { RateButton, RateEventDialog } from "@/components/RateEventDialog";
import type { EventReview } from "@/components/EventReviews";

export default function CustomerBookings() {
  const { t, lang } = usePrefs();
  const user = useAuth().user as User;
  const online = useOnline();
  const [list, setList] = useState<Booking[]>(() => loadCachedBookings(user.user_id));
  const [fromCache, setFromCache] = useState(false);
  const [open, setOpen] = useState<Booking | null>(null);
  const [cancelling, setCancelling] = useState<Booking | null>(null);
  const [changing, setChanging] = useState<Booking | null>(null);
  const [disputing, setDisputing] = useState<Booking | null>(null);
  const dispute = async (message: string) => {
    try {
      await api.post(`/bookings/${disputing!.booking_id}/dispute`, { message });
      toast.success(t("b6.disputeSent"));
      setDisputing(null);
    } catch (e) {
      toast.error(errorText(e, t));
    }
  };
  const [params, setParams] = useSearchParams();
  const [rating, setRating] = useState<Booking | null>(null);
  const [myReviews, setMyReviews] = useState<Record<string, EventReview>>({});
  const loadReviews = () => api.get("/me/event-reviews").then((r) => setMyReviews(Object.fromEntries(r.data.map((x: EventReview) => [x.event_id, x])))).catch(() => {});
  useEffect(() => { loadReviews(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = params.get("ticket");
    const b = id && list.find((x) => x.booking_id === id);
    if (b) {
      setOpen(b);
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, list]);

  const load = () =>
    api
      .get("/bookings/me")
      .then((r) => {
        setList(r.data);
        saveCachedBookings(user.user_id, r.data);
        setFromCache(false);
      })
      .catch(() => setFromCache(true));

  useLive(() => load());
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  const material = async (b: Booking, accept: boolean) => {
    try {
      await api.post(`/bookings/${b.booking_id}/material-change`, { accept });
      toast.success(t(accept ? "b5.done" : "bookings.cancelled"));
      load();
    } catch (e) {
      toast.error(errorText(e, t));
    }
  };

  const cancel = async (b: Booking, reason: string) => {
    try {
      await api.post(`/bookings/${b.booking_id}/cancel`, { reason });
      toast.success(t("bookings.cancelled"));
      setCancelling(null);
      window.dispatchEvent(new Event("wn:notify"));
      load();
    } catch (e) {
      toast.error(errorText(e, t));
    }
  };

  return (
    <div data-testid="customer-bookings-page">
      <PageTitle title={t("bookings.title")} />
      <RestrictionBanner />
      {(!online || fromCache) && (
        <div data-testid="bookings-offline-banner" className="mb-4 flex items-center gap-2 rounded-2xl bg-amber-500/15 text-amber-700 dark:text-amber-400 p-3 text-sm font-semibold">
          <WifiOff className="h-4 w-4 shrink-0" />
          {t("bookings.offline")}
        </div>
      )}
      {list.length === 0 ? (
        <div data-testid="bookings-empty" className="text-center py-16 space-y-4">
          <div className="mx-auto h-20 w-20 rounded-[1.75rem] bg-primary/10 text-primary grid place-items-center rotate-6">
            <Ticket className="h-10 w-10" />
          </div>
          <p className="text-muted-foreground">{t("bookings.empty")}</p>
          <Link to="/app" data-testid="bookings-explore-link" className="inline-flex h-11 px-6 items-center rounded-full bg-primary text-primary-foreground font-bold">
            {t("hero.cta")}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {list.map((b) => (
            <BookingRow key={b.booking_id} b={b} onOpen={() => setOpen(b)} extra={online && <RateButton b={b} mine={myReviews[b.event_id || ""]} onClick={() => setRating(b)} />} {...(online && !fromCache ? {
              onCancel: () => setCancelling(b), onChange: () => setChanging(b), onMaterial: (a: boolean) => material(b, a), onDispute: () => setDisputing(b) } : {})} />
          ))}
        </div>
      )}
      {cancelling && (
        <ReasonDialog testid="customer-cancel-dialog" title={t("b5.cancelTitle")} required={false} onClose={() => setCancelling(null)} onSubmit={(r) => cancel(cancelling, r)}
          note={cancelling.status === "confirmed" && cancelling.terms_snapshot?.cancel_until && (
            new Date(cancelling.terms_snapshot.cancel_until) < new Date()
              ? <span className="text-primary font-semibold" data-testid="cancel-late-warning">{t("b5.lateWarn").replace("{time}", fmtDateTime(cancelling.terms_snapshot.cancel_until, lang))}</span>
              : t("b5.cancelUntil").replace("{time}", fmtDateTime(cancelling.terms_snapshot.cancel_until, lang)))} />
      )}
      {disputing && <ReasonDialog testid="dispute-dialog" title={t("b6.disputeTitle")} onClose={() => setDisputing(null)} onSubmit={dispute} />}
      {changing && <GuestsChangeDialog b={changing} onClose={() => setChanging(null)} onDone={load} />}
      {rating && <RateEventDialog b={rating} mine={myReviews[rating.event_id || ""]} onClose={() => setRating(null)} onDone={() => { setRating(null); loadReviews(); }} />}
      <TicketDialog booking={open} online={online} onClose={() => setOpen(null)} />
    </div>
  );
}
